import { Controller, useForm, useFormContext } from 'react-hook-form';
import { CustomInput, DatePicker } from '../common/forms';
import React, { useState } from 'react';
import { Translate, useTranslate } from '~/i18n';
import { successMessage } from '~/utils/toasts';
import InputMask from 'react-input-mask';

import Axios from '~/utils/axios';
import { DateTime } from 'luxon';
import { File } from '~/components/common/forms';
import { RequestFooter } from './request-footer';
import { sentryCaptureException } from '~/errors/sentry';
import { useRouter } from 'next/router';
import { toBase64 } from '~/utils/to-base-64';
import { renderErrorsToToaster } from './render-error-to-toaster';
import { isBase64 } from '~/utils/isBase64';
import { isBase64Encoded } from '~/utils/isBase64Encoded';

export const AdditionalDocuments = () => {
   const { translate, lang } = useTranslate();
   const router = useRouter();
   const [loading, setLoading] = useState(false);
   const { id, applicationNo } = router.query;

   const { reset, getValues } = useFormContext<any>();
   const formValues = getValues();

   const {
      register,
      control,
      formState: { errors },
      handleSubmit,
      setValue,
      setError,
      watch,
   } = useForm<any>({
      defaultValues: {
         ...formValues,
      },
   });
   const submitForm = async (values: any) => {
      setLoading(true);
      const iqamaCopyFile = values.iqamaCopy[0];
      let iqamaCopy;
      if (iqamaCopyFile || values.iqamaCopy) {
         iqamaCopy =
            iqamaCopyFile && (isBase64(values?.iqamaCopy) || isBase64Encoded(values?.iqamaCopy))
               ? values?.iqamaCopy
               : await toBase64(iqamaCopyFile);
      } else {
         setError('iqamaCopy', {
            message: translate({ id: 'validation:required' }),
         });
         setLoading(false);
      }

      const body = {
         visitors: [{ ...values, iqamaCopy }],
         current_step: 'applicant_additional_documents',
      };
      try {
         await Axios.put(`/dhamen/v1/visa-request/${id}/visitors/${applicationNo}`, body);
         reset({ ...getValues(), ...values });
         successMessage(translate({ id: 'visasManager:submit message' }));
         router.push(
            `/${lang}/umrah-requests/${id}/add-applicant/${applicationNo}/edit/more-information`
         );
      } catch (error: any) {
         console.log(error);
         sentryCaptureException(error);
         renderErrorsToToaster(error?.response?.data);
      }
      setLoading(false);
   };

   return (
      <>
         <div>
            <div className="mb-8 text-xl font-bold text-primary">
               <Translate id="visasManager:additional documents" />
            </div>

            <div className="w-full">
               <form onSubmit={handleSubmit(submitForm)}>
                  <div className="mb-5">
                     <File
                        id="iqamaCopy"
                        label={
                           <div className="flex items-center gap-1 font-semibold sm:text-base">
                              <div>
                                 <Translate id={'visasManager:upload id photo'} />
                              </div>
                           </div>
                        }
                        {...register('iqamaCopy')}
                        defaultImage={getValues('iqamaCopy') || formValues?.iqamaCopy || ''}
                        groupClassName="!mb-8 col-span-2 flex flex-col justify-between"
                        error={(errors.iqamaCopy as any)?.message}
                        setError={(message: string) => setError('iqamaCopy', { message })}
                        onFileDelete={() => {
                           setValue('iqamaCopy', undefined);
                        }}
                        min_size_limit={0.0033}
                        size_limit={0.1}
                        size_limit_type="KB"
                        // dimensions={{ width: 200, height: 200 }}
                        is_required
                        isEncodedBase64={Boolean(isBase64Encoded(watch('iqamaCopy')))}
                        accept="image/jpeg,image/gif,image/png,image/x-eps"
                        description={
                           <ul className="list-disc list-inside">
                              <li>
                                 <Translate id="visasManager:id photo note" />
                              </li>
                              <li>
                                 <Translate
                                    id="validation:max file size with type"
                                    values={{
                                       amount: '100',
                                       type: 'kb',
                                    }}
                                 />
                              </li>
                              <li>
                                 <Translate
                                    id="validation:min file size with type"
                                    values={{
                                       amount: '3.3',
                                       type: 'kb',
                                    }}
                                 />
                              </li>
                              {/* <li>
                                 <Translate
                                    id="visasManager:upload dimensions"
                                    values={{
                                       width: '200',
                                       height: '200',
                                    }}
                                 />
                              </li> */}
                           </ul>
                        }
                     />
                  </div>
                  <div className="mb-5">
                     <Controller
                        control={control}
                        name="iqamaId"
                        rules={{
                           validate: value => value > 0 || translate({ id: 'validation:required' }),
                           minLength: {
                              value: 3,
                              message: translate({
                                 id: 'validation:min characters',
                                 values: {
                                    count: String(3),
                                 },
                              }),
                           },
                           maxLength: {
                              value: 15,
                              message: translate({
                                 id: 'validation:max characters',
                                 values: {
                                    count: String(15),
                                 },
                              }),
                           },
                        }}
                        render={({ field: { onBlur, onChange, ref, value } }) => (
                           <InputMask
                              mask="9999999999999999"
                              maskChar=""
                              onChange={onChange}
                              onBlur={onBlur}
                              defaultValue={value}>
                              {(props: any) => (
                                 <CustomInput
                                    {...props}
                                    defaultValue={value}
                                    inputMode="numeric"
                                    id="iqamaId"
                                    label={<Translate id="visasManager:id number" />}
                                    placeholder={translate({ id: 'visasManager:enter id number' })}
                                    isRequired
                                    error={errors.iqamaId?.message}
                                    ref={ref}
                                    description={translate({ id: 'visasManager:id number note' })}
                                 />
                              )}
                           </InputMask>
                        )}
                     />
                  </div>

                  <div className="mb-5">
                     <Controller
                        control={control}
                        name="iqamaExpiryDate"
                        rules={{ required: translate({ id: 'validation:required' }) }}
                        render={({ field: { onChange, value } }) => (
                           <DatePicker
                              placeholderText={translate({
                                 id: 'common:date',
                              })}
                              id="iqamaExpiryDate"
                              minDate={new Date(new Date().getTime() + 24 * 60 * 60 * 1000)}
                              label={translate({ id: 'visasManager:id expiry date' })}
                              value={value?.split?.('T')?.[0] || value}
                              error={errors.iqamaExpiryDate?.message}
                              onChange={val => {
                                 const d = DateTime.fromJSDate(val as Date);
                                 onChange(d.toFormat('yyyy-MM-dd'));
                              }}
                              description={translate({ id: 'visasManager:id expiry date note' })}
                              isRequired
                           />
                        )}
                     />
                  </div>
               </form>
            </div>
         </div>
         <RequestFooter
            fwdBtnHandler={handleSubmit(submitForm)}
            fwdBtnLabel={translate({ id: 'common:next' })}
            cancelBtnHandler={() => router.back()}
            loading={loading}
         />
      </>
   );
};
